import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import formStyles from '~/styles/form.module.css';
import promoCodeStyles from '~/styles/promo-code.module.css';
import FormGroup from '~/components/Form/FormGroup';
import Button from '~/components/General/Button';
import usePromoCodeMutation from '~/hooks/bookingFlow/usePromoCodeMutation';
import useTranslation from '~/hooks/useTranslation';
import FormLabel from '~/components/Form/FormLabel';
import { useOptions } from '~/hooks/useOptions';
import { InferredBookingFlowOptions } from '~/types/options';

type Props = {
  registerField: string;
};

export default function PromoCode({
  registerField,
}: Props) {
  const translate = useTranslation();
  const fieldId = useId();
  const options = useOptions() as InferredBookingFlowOptions;
  const promoCodeMutation = usePromoCodeMutation();
  const [internalValue, setInternalValue] = useState('');
  const { setValue } = useFormContext();

  const promoCodeValue = useWatch({ name: registerField });

  const onPromoCodeChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInternalValue(event.target.value);
  }, []);

  const onPromoCodeSubmit = useCallback(async () => {
    if (!internalValue) {
      setValue(registerField, '');
      promoCodeMutation.reset();
      return;
    }

    // Send the promo code to the backend. The response and status will be
    // available on the promoCodeMutation.data object.
    promoCodeMutation.mutate({
      promo_code: internalValue,
      locale: options.locale,
    });
  }, [internalValue, promoCodeMutation, options.locale, registerField, setValue]);

  // If the promo code value is changed on the form context, update the
  // internal value.
  useEffect(() => {
    setInternalValue(promoCodeValue ?? '');
  }, [promoCodeValue]);

  // If the promo code is successfully applied, update the form context with the
  // promo code.
  useEffect(() => {
    if (promoCodeMutation.isSuccess) {
      setValue(registerField, promoCodeMutation.data?.promo_code);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCodeMutation.isSuccess, promoCodeMutation.data?.promo_code]);

  // Get the validation error from the promo code mutation error object.
  const validationError = useMemo(() => {
    if (promoCodeMutation.error && 'validationErrors' in promoCodeMutation.error) {
      if (promoCodeMutation.error.validationErrors?.promo_code) {
        return promoCodeMutation.error.validationErrors?.promo_code;
      }
    }

    return null;
  }, [promoCodeMutation.error]);

  return (
    <FormGroup>
      <FormLabel htmlFor={fieldId}>
        {translate('bookingFlow.information.promo_code')}
      </FormLabel>

      <div className={promoCodeStyles.promoCodeWrapper}>
        <input
          className={formStyles.formControl}
          id={fieldId}
          type="text"
          value={internalValue}
          onChange={onPromoCodeChange}
          readOnly={promoCodeMutation.isPending}
        />

        <Button
          type="button"
          onClick={onPromoCodeSubmit}
          disabled={promoCodeMutation.isPending}
          styleType="secondary"
        >
          {translate('bookingFlow.information.apply_promo_code')}
        </Button>
      </div>

      {promoCodeMutation.isSuccess && promoCodeMutation.data.valid && (
        <div className={formStyles.formControlSuccess} role="alert">
          {translate('bookingFlow.information.promo_code_success')}
        </div>
      )}

      {validationError && (
        <div className={formStyles.formControlError} role="alert">
          {validationError}
        </div>
      )}
    </FormGroup>
  );
}
